import { useMemo, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { GradualSpacing } from "@/components/ui/gradual-spacing";
import GlobalHeader from "@/components/GlobalHeader";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { usePageSEO } from "@/hooks/use-page-seo";
import { buildMusicEventSchema } from "@/lib/event-schema";
import { useMobileOnceInView } from "@/hooks/use-mobile-once-in-view";
import { PHOTOS } from "@/lib/photos";
import boatsGraphic from "@/assets/yrd-boats-graphic.svg";
import Footer from "@/components/Footer";

const faqItems = [
  { q: "What is Yacht Rock Detroit?", a: "Yacht Rock Detroit is a two-day waterfront music festival in Detroit, MI, featuring live yacht rock performances at The Roostertail. It takes place Friday, July 17th and Saturday, July 18th, 2026." },
  { q: "Where is the festival held?", a: "The Roostertail, located at 100 Marquette Dr, Detroit, MI 48214 — directly on the Detroit River, minutes from downtown." },
  { q: "How much are tickets?", a: "General Admission is $219 for the full two-day weekend. VIP Admiral's Pass is $450 and includes premium viewing, open bar, free food, exclusive lounge access, and VIP restrooms. Single-day passes are also available from $149." },
  { q: "When do tickets go on sale?", a: "Tickets are on sale now. Visit our tickets page to purchase General Admission ($219), VIP Admiral's Pass ($450), or a single-day pass ($149)." },
  { q: "Who is performing at Yacht Rock Detroit 2026?", a: "The 2026 lineup is live! Three Dog Night, Pablo Cruise, Ambrosia, and more perform at The Roostertail. See the full lineup at yachtrockdetroit.com/lineup." },
  { q: "Is there parking at the venue?", a: "Yes. Free and paid parking options are available near The Roostertail. Rideshare drop-off is also convenient — the venue is right off Jefferson Ave." },
  { q: "Can I arrive by boat?", a: "Yes. The Roostertail has dock access and a Boater's Balcony. Visit the festival guide page for details on boat access and dock reservations." },
  { q: "What should I wear?", a: "\"Yacht casual\" — think linen shirts, boat shoes, sundresses, sunglasses. Captain's hats optional but appreciated. It's an outdoor waterfront event, so dress for the weather and the vibes." },
  { q: "Is the festival rain or shine?", a: "Yes. Yacht Rock Detroit is a rain-or-shine event. The show goes on regardless of weather." },
  { q: "Are there hotel packages available?", a: "Official hotel packages for 2026 are being finalized. Check the travel page for updates and partner hotel information." },
  { q: "Is there food and drink available?", a: "Yes. The festival features curated food vendors and full bars with craft cocktails and local beer. No outside food or beverages are permitted." },
  { q: "Is the event all ages?", a: "Yacht Rock Detroit is a 21+ event." },
];

const Festival = () => {
  const headerRef = useRef<HTMLElement>(null);
  const isHeaderInView = useMobileOnceInView(headerRef, { margin: "-100px" });

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' as ScrollBehavior });
  }, []);

  const jsonLd = useMemo(() => ([
    buildMusicEventSchema({
      url: "https://yachtrockdetroit.com/festival",
      description: "Yacht Rock Detroit is a two-day waterfront music festival in Detroit, MI at The Roostertail — July 17-18, 2026. Nine live acts including Three Dog Night, Pablo Cruise, Ambrosia, Yacht Rock Revue, Elliot Lurie, Peter Beckett, Walter Egan, John Ford Coley, and Seven Wonders. Sunset concerts on the Detroit River.",
    }),
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": faqItems.map(item => ({
        "@type": "Question",
        "name": item.q,
        "acceptedAnswer": {
          "@type": "Answer",
          "text": item.a,
        },
      })),
    }
  ]), []);

  usePageSEO({
    title: "Yacht Rock Detroit 2026 Festival | Detroit Waterfront Concert — July 17-18",
    description: "Everything you need to know about Yacht Rock Detroit 2026 — a two-day waterfront concert festival at The Roostertail in Detroit. Featuring Three Dog Night, Pablo Cruise, Ambrosia & more. Summer concerts Detroit, July 17-18, 2026.",
    canonical: "https://yachtrockdetroit.com/festival",
    keywords: "yacht rock detroit, detroit yacht rock festival, detroit waterfront music festival, concert detroit 2026, summer concerts detroit, detroit music festival, upcoming concerts detroit, yacht rock concert, live music detroit waterfront, roostertail detroit events",
    ogImage: PHOTOS.CROWD.sunsetStageView,
    jsonLd: jsonLd,
  });

  return (
    <div className="min-h-screen bg-yacht-cream">
      <GlobalHeader heroRef={headerRef} />

      {/* Hero */}
      <section
        ref={headerRef}
        className="relative min-h-[60vh] md:min-h-[70vh] bg-yacht-blue overflow-hidden flex items-end"
      >
        {/* Noise */}
        <div
          className="absolute inset-0 opacity-[0.12] pointer-events-none"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")`,
          }}
        />
        <img
          src={boatsGraphic}
          alt=""
          className="absolute pointer-events-none z-0 w-[800%] left-1/2 -translate-x-1/2 top-0 sm:w-[150%] sm:-top-[30%] lg:w-[90%] lg:-left-[10%] lg:-top-[30%] lg:translate-x-0"
          style={{ opacity: 0.15, filter: 'brightness(0) invert(1)' }}
        />

        <div className="relative z-20 px-6 md:px-16 pb-16 md:pb-20 pt-28">
          <motion.p
            className="text-yacht-yellow/80 text-sm md:text-base font-medium tracking-[0.3em] uppercase mb-4"
            initial={{ opacity: 0, y: 20 }}
            animate={isHeaderInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
            transition={{ duration: 0.6 }}
          >
            July 17–18, 2026 · The Roostertail
          </motion.p>
          <h1 className="text-[10vw] md:text-[6vw] lg:text-[5vw] font-bold text-white leading-[0.9] tracking-tight max-w-4xl">
            <GradualSpacing text="Detroit's Original Waterfront Yacht Rock Festival" duration={0.4} delayMultiple={0.02} />
          </h1>
        </div>
      </section>

      {/* Yellow strip */}
      <section className="relative py-5 bg-yacht-yellow overflow-hidden">
        <div
          className="absolute inset-0 opacity-[0.06] pointer-events-none"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")`,
          }}
        />
        <p className="relative z-10 text-yacht-charcoal/80 text-lg md:text-xl font-medium text-center" style={{ fontFamily: 'Georgia, serif' }}>
          GA $219 · VIP $450 · Single Day $149 · On the Water
        </p>
      </section>

      {/* Main Content */}
      <article className="py-16 md:py-24 px-6 md:px-16">
        <div className="max-w-3xl mx-auto">

          {/* What Is Yacht Rock Detroit? */}
          <motion.section
            className="mb-16"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-50px" }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-2xl md:text-3xl font-bold text-yacht-charcoal mb-6">What Is Yacht Rock Detroit?</h2>
            <div className="space-y-4 text-yacht-charcoal/80 text-base md:text-lg leading-relaxed" style={{ fontFamily: 'Georgia, serif' }}>
              <p>Yacht Rock Detroit is a two-day waterfront music festival celebrating the smoothest genre ever created. Set along the Detroit River at The Roostertail, the festival brings together live yacht rock acts, golden-hour sunsets, craft cocktails, and a crowd that knows every word to "Sailing."</p>
              <p>This isn't a tribute act in a hotel ballroom. Yacht Rock Detroit is a purpose-built festival experience — real stages, curated food and drink, waterfront views, and a production level that matches the music's polish. If you've ever wanted to hear "Africa" while watching the sun drop behind the Ambassador Bridge, this is your weekend.</p>
            </div>
          </motion.section>

          {/* When and Where */}
          <motion.section
            className="mb-16"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-50px" }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-2xl md:text-3xl font-bold text-yacht-charcoal mb-6">When and Where</h2>
            <div className="space-y-4 text-yacht-charcoal/80 text-base md:text-lg leading-relaxed" style={{ fontFamily: 'Georgia, serif' }}>
              <p><strong className="text-yacht-charcoal">Dates:</strong> Friday, July 17th & Saturday, July 18th, 2026</p>
              <p><strong className="text-yacht-charcoal">Venue:</strong> The Roostertail — 100 Marquette Dr, Detroit, MI 48214</p>
              <p><strong className="text-yacht-charcoal">Hours:</strong> Gates open in the afternoon. Music runs through sunset and into the evening both days.</p>
              <p>The Roostertail sits directly on the Detroit River, just minutes from downtown. The venue's mid-century waterfront setting is part of what makes this festival unlike anything else in the Midwest. You're not standing in a parking lot or a field — you're on the water.</p>
            </div>
          </motion.section>

          {/* Why Detroit? Why the Waterfront? */}
          <motion.section
            className="mb-16"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-50px" }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-2xl md:text-3xl font-bold text-yacht-charcoal mb-6">Why Detroit? Why the Waterfront?</h2>
            <div className="space-y-4 text-yacht-charcoal/80 text-base md:text-lg leading-relaxed" style={{ fontFamily: 'Georgia, serif' }}>
              <p>Yacht rock was built for the water. The genre's entire DNA — sailboats, ocean breezes, West Coast ease — translates perfectly to a riverfront setting. Detroit's waterfront is one of the most underrated stretches of shoreline in the country, and The Roostertail has been hosting events on it since 1958.</p>
              <p>The combination of live yacht rock and a real waterfront venue isn't something you'll find at any other festival. Most yacht rock events happen indoors. Yacht Rock Detroit happens where the music was always meant to be heard — outside, near the water, as the sun goes down.</p>
              <p>Detroit also brings something no coastal city can replicate: a genuine, no-pretense energy. The crowd here isn't posing. They're singing, dancing, and having the best weekend of their summer.</p>
            </div>
          </motion.section>

          {/* The Music */}
          <motion.section
            className="mb-16"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-50px" }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-2xl md:text-3xl font-bold text-yacht-charcoal mb-6">The Music</h2>
            <div className="space-y-4 text-yacht-charcoal/80 text-base md:text-lg leading-relaxed" style={{ fontFamily: 'Georgia, serif' }}>
              <p>Yacht Rock Detroit features a curated lineup of live acts performing the classics — Steely Dan, Doobie Brothers, Hall & Oates, Christopher Cross, Toto, Michael McDonald, and deep cuts you forgot you loved. The festival books performers who understand the genre, not just cover bands running through a setlist.</p>
              <p><strong className="text-yacht-charcoal">The 2026 lineup is live</strong> — featuring Three Dog Night, Pablo Cruise, Ambrosia, and more. <Link to="/lineup" className="text-yacht-blue underline hover:text-yacht-blue/80">View the full lineup</Link>.</p>
              <p>Previous years have featured full bands with horn sections, multi-act bills across both days, and surprise sit-ins that only happen at a festival this focused on one genre.</p>
            </div>
          </motion.section>

          {/* Tickets and Pricing */}
          <motion.section
            className="mb-16"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-50px" }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-2xl md:text-3xl font-bold text-yacht-charcoal mb-6">Tickets and Pricing</h2>
            <div className="grid md:grid-cols-2 gap-6 mb-6">
              <div className="relative bg-white rounded-2xl p-8 overflow-hidden">
                <div
                  className="absolute inset-0 opacity-[0.08] pointer-events-none"
                  style={{
                    backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")`,
                  }}
                />
                <div className="relative z-10">
                  <h3 className="text-xl font-bold text-yacht-charcoal mb-2">General Admission</h3>
                  <p className="text-3xl font-bold text-yacht-blue mb-4">$219</p>
                  <p className="text-yacht-charcoal/70" style={{ fontFamily: 'Georgia, serif' }}>Full two-day access, all stages, waterfront grounds</p>
                </div>
              </div>
              <div className="relative bg-yacht-charcoal rounded-2xl p-8 overflow-hidden">
                <div className="relative z-10">
                  <h3 className="text-xl font-bold text-white mb-2">VIP</h3>
                  <p className="text-3xl font-bold text-yacht-yellow mb-4">$450</p>
                  <p className="text-white/70" style={{ fontFamily: 'Georgia, serif' }}>Everything in GA + premium viewing areas, dedicated bar, exclusive lounge, VIP restrooms</p>
                </div>
              </div>
            </div>
            <p className="text-yacht-charcoal/80 text-base md:text-lg leading-relaxed" style={{ fontFamily: 'Georgia, serif' }}>
              <strong className="text-yacht-charcoal">Tickets are available now.</strong> Visit our <Link to="/tickets" className="text-yacht-blue underline hover:text-yacht-blue/80">tickets page</Link> to secure your spot before they sell out. Yacht Rock Detroit sold out in previous years — don't wait.
            </p>
          </motion.section>

          {/* What to Expect */}
          <motion.section
            className="mb-16"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-50px" }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-2xl md:text-3xl font-bold text-yacht-charcoal mb-6">What to Expect at the Festival</h2>
            <div className="space-y-4 text-yacht-charcoal/80 text-base md:text-lg leading-relaxed" style={{ fontFamily: 'Georgia, serif' }}>
              <p><strong className="text-yacht-charcoal">The Vibe:</strong> Think sunset cocktail party meets outdoor concert. The dress code is "yacht casual" — linen shirts, boat shoes, and sunglasses are encouraged but not required. You'll see everything from captain's hats to sundresses.</p>
              <p><strong className="text-yacht-charcoal">Food & Drink:</strong> Curated food vendors and full bars. Expect craft cocktails, local beer, and food options that go beyond standard festival fare.</p>
              <p><strong className="text-yacht-charcoal">The Setting:</strong> The Roostertail's grounds wrap along the river. You'll have views of the water from almost everywhere on site. As the sun sets behind the Detroit skyline, the whole venue turns golden — it's the single best moment of the weekend, every year.</p>
              <p><strong className="text-yacht-charcoal">Boat Access:</strong> The Roostertail has dock access. If you're arriving by boat, details on the Boater's Balcony and dock reservations are available on our <Link to="/guide" className="text-yacht-blue underline hover:text-yacht-blue/80">festival guide</Link>.</p>
            </div>
          </motion.section>

          {/* Who Is This For */}
          <motion.section
            className="mb-16"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-50px" }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-2xl md:text-3xl font-bold text-yacht-charcoal mb-6">Who Is This Festival For?</h2>
            <div className="space-y-4 text-yacht-charcoal/80 text-base md:text-lg leading-relaxed" style={{ fontFamily: 'Georgia, serif' }}>
              <p>Yacht Rock Detroit draws a mix: music fans in their 30s through 60s, couples looking for a date-night-level weekend, groups of friends who've been quoting "Yacht Rock" sketches for years, and Detroit locals who want a premium summer event that isn't the same thing as every other festival.</p>
              <p>If you know what yacht rock is, you'll love this. If you don't, you'll leave as a convert. The genre is having a massive cultural moment — streaming numbers for classic yacht rock tracks have surged, and festivals like this one are leading the revival.</p>
            </div>
          </motion.section>

          {/* Getting There */}
          <motion.section
            className="mb-16"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-50px" }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-2xl md:text-3xl font-bold text-yacht-charcoal mb-6">Getting There</h2>
            <div className="space-y-4 text-yacht-charcoal/80 text-base md:text-lg leading-relaxed" style={{ fontFamily: 'Georgia, serif' }}>
              <p>The Roostertail is located at 100 Marquette Dr, just east of downtown Detroit along the river.</p>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong className="text-yacht-charcoal">Driving:</strong> Free and paid parking available nearby. Rideshare drop-off is easy — the venue is right off Jefferson Ave.</li>
                <li><strong className="text-yacht-charcoal">Hotels:</strong> Several downtown Detroit hotels are within a 10-minute drive. We're working on official hotel packages for 2026 — check the <Link to="/travel" className="text-yacht-blue underline hover:text-yacht-blue/80">travel page</Link> for updates.</li>
                <li><strong className="text-yacht-charcoal">By Boat:</strong> Dock access available. See the <Link to="/guide" className="text-yacht-blue underline hover:text-yacht-blue/80">festival guide</Link> for details.</li>
              </ul>
            </div>
          </motion.section>

          {/* Closing */}
          <motion.section
            className="mb-20"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-50px" }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-2xl md:text-3xl font-bold text-yacht-charcoal mb-6">A Detroit Yacht Rock Festival — Not Just Another Concert</h2>
            <div className="space-y-4 text-yacht-charcoal/80 text-base md:text-lg leading-relaxed" style={{ fontFamily: 'Georgia, serif' }}>
              <p>There are yacht rock cover nights at bars across the country. Yacht Rock Detroit is something different. It's a full-scale, multi-day festival dedicated to the genre, set on one of the best waterfront venues in the Midwest, with a production team that treats yacht rock with the respect it deserves.</p>
              <p>This is the only festival of its kind in Detroit. It's one of the only ones of its kind anywhere.</p>
              <p className="text-yacht-charcoal font-semibold text-xl" style={{ fontFamily: 'Inter, sans-serif' }}>Friday, July 17th. Saturday, July 18th. The Roostertail. Detroit's waterfront. The smoothest weekend of your summer.</p>
            </div>
          </motion.section>

          {/* FAQ Section */}
          <motion.section
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-50px" }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-2xl md:text-3xl font-bold text-yacht-charcoal mb-8 pb-2 border-b-2 border-yacht-blue/30">
              Frequently Asked Questions
            </h2>
            <Accordion type="single" collapsible className="space-y-4">
              {faqItems.map((item, i) => (
                <AccordionItem
                  key={i}
                  value={`faq-${i}`}
                  className="relative rounded-2xl overflow-hidden border-0"
                >
                  <div className="absolute inset-0 bg-white rounded-2xl" />
                  <div
                    className="absolute inset-0 opacity-[0.08] pointer-events-none rounded-2xl"
                    style={{
                      backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")`,
                    }}
                  />
                  <AccordionTrigger className="relative z-10 px-6 md:px-8 py-5 md:py-6 text-left text-yacht-charcoal text-base md:text-lg font-semibold hover:no-underline [&[data-state=open]]:text-yacht-blue">
                    {item.q}
                  </AccordionTrigger>
                  <AccordionContent className="relative z-10 px-6 md:px-8 pb-6 md:pb-8 pt-0 text-yacht-charcoal/70 text-base md:text-lg leading-relaxed" style={{ fontFamily: 'Georgia, serif' }}>
                    {item.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </motion.section>

        </div>
      </article>

      {/* Bottom CTA */}
      <section className="py-16 bg-yacht-charcoal">
        <div className="max-w-4xl mx-auto text-center px-8">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Don't Miss It
          </h2>
          <p className="text-white/70 text-lg mb-8" style={{ fontFamily: 'Georgia, serif' }}>
            Stay in the loop for lineup updates and exclusive announcements.
          </p>
          <Link
            to="/tickets"
            className="inline-block px-8 py-3.5 bg-yacht-yellow text-yacht-charcoal font-bold text-lg rounded-full hover:scale-105 transition-transform shadow-lg"
          >
            Get Tickets
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Festival;
